/**
 * A retenção: apagar o material cifrado de uma credencial que já não precisa ficar guardada.
 *
 * **Por que este botão existe, e por que ele não existia.** O ADR 0006 pede política de retenção,
 * e o serviço sempre descreveu o descarte como "o ato de retenção de quem opera o cofre" — mas a
 * rota cobrava `infra:responder`, que a matriz da spec §4 dá ao admin do cliente e ao TI, e a
 * nenhum papel interno. O ato documentado era inalcançável para quem o documento diz que o
 * pratica, e alcançável só por quem podia destruir unilateralmente a credencial de que a
 * implantação depende. Com o descarte passando a exigir `credencial:revelar`, quem opera o cofre
 * passou a poder — e uma tela que continuasse dizendo "aqui não se descarta" estaria mentindo.
 *
 * **Confirmação em dois passos, e não um `confirm()`.** Descartar é irreversível: depois dele
 * ninguém revela aquele valor de novo, nem o cliente regrava se a trilha já estiver enviada. O
 * segundo passo é a diferença entre um clique errado e uma decisão. Não usa `window.confirm`
 * porque o diálogo do navegador não diz o que está prestes a acontecer com as palavras deste
 * portal — e é o texto que faz a pessoa entender que a resposta do formulário e o histórico de
 * revelações **ficam**, e só o material some.
 *
 * **O que este componente não promete.** Que os bytes sumiram do banco. `UPDATE` no Postgres
 * escreve tupla nova e deixa a antiga até o `VACUUM`, e ela sobrevive mais tempo ainda em WAL,
 * réplica e backup; enquanto a chave mestra for a mesma, quem tiver um backup e a chave abre o
 * que foi descartado. O texto da tela diz "não sai mais por aqui", que é o que é verdade.
 *
 * A autorização não mora aqui: quem não tem `credencial:revelar` não recebe este componente, mas
 * isso é cortesia. A negativa acontece no serviço, e é lá que o teste dela roda.
 */

import { useState } from "react";
import { Aviso, Botao } from "../ui";
import { descartarSegredo, useEscritaDoCofre } from "../../nucleo/cofre";

export interface PropsDoDescarte {
  segredoId: string;
  /** Como a credencial é chamada — para a pessoa saber o que está prestes a apagar. */
  rotulo: string;
  /** Chamado depois de um descarte bem-sucedido, para a listagem ser relida. */
  aoDescartar?: () => void;
}

export function DescarteDoSegredo({ segredoId, rotulo, aoDescartar }: PropsDoDescarte) {
  const [confirmando, setConfirmando] = useState(false);
  const escrita = useEscritaDoCofre();

  async function descartar() {
    const feito = await escrita.executar(() => descartarSegredo(segredoId));
    if (feito) {
      setConfirmando(false);
      aoDescartar?.();
    }
  }

  if (!confirmando) {
    return (
      <div className="descarte">
        {escrita.erro ? (
          <Aviso tom="erro" className="mb-2" data-testid="erro-do-descarte">
            {escrita.erro}
          </Aviso>
        ) : null}
        <Botao
          variante="fantasma"
          tamanho="pequeno"
          data-testid="pedir-descarte"
          onClick={() => {
            escrita.limparErro();
            setConfirmando(true);
          }}
        >
          Descartar
        </Botao>
      </div>
    );
  }

  return (
    <div className="descarte descarte--confirmando" data-testid="confirmacao-do-descarte">
      <p className="descarte__pergunta" role="status">
        <b>Descartar “{rotulo}”?</b> O material cifrado é apagado e ninguém mais revela este
        valor — nem você. A resposta que o cliente deu no formulário e o histórico de quem já viu
        esta credencial <b>ficam</b>: o descarte apaga o segredo, não a prova.
      </p>
      <p className="nota-rodape mb-2">
        Se a implantação ainda for precisar deste acesso, peça a credencial de novo ao cliente
        antes de descartar — com a trilha já enviada, ele não consegue regravá-la sozinho.
      </p>
      {escrita.erro ? (
        <Aviso tom="erro" className="mb-2" data-testid="erro-do-descarte">
          {escrita.erro}
        </Aviso>
      ) : null}
      <div className="descarte__acoes">
        <Botao
          tamanho="pequeno"
          data-testid="confirmar-descarte"
          disabled={escrita.executando}
          onClick={() => void descartar()}
        >
          {escrita.executando ? "Descartando…" : "Descartar mesmo assim"}
        </Botao>
        <Botao
          variante="fantasma"
          tamanho="pequeno"
          data-testid="cancelar-descarte"
          disabled={escrita.executando}
          onClick={() => {
            escrita.limparErro();
            setConfirmando(false);
          }}
        >
          Cancelar
        </Botao>
      </div>
    </div>
  );
}
